import { useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Share2, Link2, Check, Facebook, Twitter, MessageCircle } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { ContentPageShell, Prose } from "@/components/ContentPageShell";

import { getPost, POSTS } from "@/content/blog";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : undefined;
  if (!post) return <Navigate to="/blog" replace />;

  const url = `https://tipomatic.app/blog/${post.slug}`;
  const others = POSTS.filter((p) => p.slug !== post.slug && p.lang === post.lang).slice(0, 3);

  return (
    <>
      <PageMeta title={`${post.title} — Tipomatic`} description={post.description} path={`/blog/${post.slug}`} type="article" />
      <Helmet>
        <html lang={post.lang} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.description,
            author: { "@type": "Person", name: post.author },
            datePublished: post.date,
            inLanguage: post.lang,
            mainEntityOfPage: url,
          })}
        </script>
      </Helmet>
      <ContentPageShell>
        <article>
          <div className="text-xs uppercase tracking-wider text-primary mb-3">
            {post.date} · {post.readingMinutes} min čtení · {post.author}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">{post.title}</h1>
          <Prose>
            <div dangerouslySetInnerHTML={{ __html: post.bodyHtml }} />
          </Prose>
        </article>

        <ShareArticle title={post.title} url={url} lang={post.lang} />

        {others.length > 0 && (
          <aside className="mt-16 pt-8 border-t border-white/10">
            <h2 className="text-lg font-semibold text-pitch-cream mb-4">Další články</h2>
            <ul className="space-y-2">
              {others.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blog/${p.slug}`} className="text-primary hover:underline">
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <div className="mt-8">
          <Link to="/blog" className="text-sm text-pitch-cream/60 hover:text-pitch-cream">
            ← {post.lang === "cs" ? "Všechny články" : "All articles"}
          </Link>
        </div>
      </ContentPageShell>
    </>
  );
}

function ShareArticle({ title, url, lang }: { title: string; url: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  const t = lang === "cs"
    ? { share: "Sdílet článek", copy: "Kopírovat odkaz", copied: "Zkopírováno" }
    : { share: "Share article", copy: "Copy link", copied: "Copied" };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t.copy, url);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: title, url });
    } catch {
      /* user cancelled */
    }
  };

  const btn = "inline-flex items-center gap-2 h-9 px-3 rounded-md border border-white/15 text-sm text-pitch-cream/80 hover:text-pitch-cream hover:border-white/30 transition-colors";

  return (
    <div className="mt-10 flex flex-wrap items-center gap-2">
      <span className="text-sm text-pitch-cream/60 mr-1">{t.share}:</span>
      {typeof navigator !== "undefined" && "share" in navigator && (
        <button type="button" onClick={nativeShare} className={btn}>
          <Share2 className="h-4 w-4" /> {t.share}
        </button>
      )}
      <a className={btn} target="_blank" rel="noopener noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}>
        <Facebook className="h-4 w-4" /> Facebook
      </a>
      <a className={btn} target="_blank" rel="noopener noreferrer" href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`}>
        <Twitter className="h-4 w-4" /> X
      </a>
      <a className={btn} target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`}>
        <MessageCircle className="h-4 w-4" /> WhatsApp
      </a>
      <button type="button" onClick={copyLink} className={btn}>
        {copied ? <Check className="h-4 w-4 text-primary" /> : <Link2 className="h-4 w-4" />}
        {copied ? t.copied : t.copy}
      </button>
    </div>
  );
}
